"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var SPACING = 20;
var _default = {
  /**
   Calculate a position for container to stay within the viewport.
   Unless otherwise specified, aligns the contentNode right, left, bottom then top moving down the list
   until the contentNode is fully visible within the viewport. Placement (right, left, bottom, top)
   can be explicitly set using the `data-placement` data attribute on the launcher node.
   Adds a class to the container based on location of the content.
    @method _calculateOptimalPosition
   @param contentNode {Node} Container node for the targetted launchNode
   @param launchNode {Node} Link node that triggered the launchNode
   @return {Object} An object with calculated x and y
   **/
  _calculateOptimalPosition: function _calculateOptimalPosition(contentNode, launchNode, viewport, placementOverride) {
    var boundary = this._getRegion(viewport),
        placement = placementOverride,
        contentNodeRegion = this._getRegion(contentNode),
        launchNodeRegion = this._getRegion(launchNode),
        rightAlignedPos = this._calculateRightAlignedPosition(launchNodeRegion, contentNodeRegion),
        leftAlignedPos = this._calculateLeftAlignedPosition(launchNodeRegion, contentNodeRegion),
        bottomAlignedPos = this._calculateBottomAlignedPosition(launchNodeRegion, contentNodeRegion, boundary),
        topAlignedPos = this._calculateTopAlignedPosition(launchNodeRegion, contentNodeRegion, boundary),
        arrow = contentNode.querySelector('.zsg-tooltip-arrow'),
        posObj;

    if (launchNodeRegion.bottom < boundary.top) {
      this._hideOpenContent();

      return null;
    }

    switch (placement) {
      case 'right':
        posObj = rightAlignedPos;
        break;

      case 'left':
        posObj = leftAlignedPos;
        break;

      case 'bottom':
        posObj = bottomAlignedPos;
        break;

      case 'top':
        posObj = topAlignedPos;
        break;

      default:
        if (rightAlignedPos.x + contentNodeRegion.width < boundary.right) {
          posObj = rightAlignedPos;

          this._repositionVertical(boundary, contentNodeRegion, posObj);
        } else if (leftAlignedPos.x - boundary.left > 0
        /* check left */
        ) {
            posObj = leftAlignedPos;

            this._repositionVertical(boundary, contentNodeRegion, posObj);
          } else if (topAlignedPos.y > boundary.top
        /* check top */
        ) {
            posObj = topAlignedPos;

            this._repositionHorizontal(boundary, contentNodeRegion, posObj);
          } else {
          posObj = bottomAlignedPos; // Bottom is the backup, because we can always make space at the bottom of the screen

          this._repositionHorizontal(boundary, contentNodeRegion, posObj);
        }

    }

    if (arrow) {
      // For bottom and top aligned tooltips the "arrow" that connects the tooltip to the launch icon may need to be adjusted.
      if (posObj.placement === 'bottom' || posObj.placement === 'top') {
        posObj.arrow = this._repositionArrowHorizontal(launchNodeRegion, posObj);
      } else {
        posObj.arrow = this._repositionArrowVertical(launchNodeRegion, posObj);
      }
    }

    return posObj;
  },
  _getRegion: function _getRegion(node) {
    return node.getBoundingClientRect();
  },
  _repositionHorizontal: function _repositionHorizontal(viewport, contentNodeRegion, posObj) {
    if (posObj.x + contentNodeRegion.width > viewport.right
    /* check right */
    ) {
        posObj.x = viewport.right - contentNodeRegion.width;
      } else if (posObj.x < viewport.left
    /* check left */
    ) {
        posObj.x = viewport.left;
      }
  },
  _repositionVertical: function _repositionVertical(viewport, contentNodeRegion, posObj) {
    var bottomCutoff = viewport.bottom || window.innerHeight;

    if (posObj.y < viewport.top
    /* check top */
    ) {
        posObj.y = viewport.top;
      } else if (posObj.y + contentNodeRegion.height > bottomCutoff
    /* check bottom */
    ) {
        posObj.y = viewport.bottom - contentNodeRegion.height; // ZM-6471 better to have tooltip overflow off the bottom of the screen.

        if (posObj.y < viewport.top
        /* check top again */
        ) {
            posObj.y = viewport.top;
          }
      }
  },

  /**
   @method _calculateRightAlignedPosition
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode.
   @private
   @return {Object} With the top and left coordinates of the contentNode and a class to apply with it.
   **/
  _calculateRightAlignedPosition: function _calculateRightAlignedPosition(launchNodeRegion, contentNodeRegion) {
    return {
      x: launchNodeRegion.right + SPACING,
      y: this._calculateVerticalPos(launchNodeRegion, contentNodeRegion),
      placement: 'right'
    };
  },

  /**
   @method _calculateLeftAlignedPosition
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode.
   @private
   @return {Object} With the top and left coordinates of the contentNode and a class to apply with it.
   **/
  _calculateLeftAlignedPosition: function _calculateLeftAlignedPosition(launchNodeRegion, contentNodeRegion) {
    return {
      x: launchNodeRegion.left - SPACING - contentNodeRegion.width,
      y: this._calculateVerticalPos(launchNodeRegion, contentNodeRegion),
      placement: 'left'
    };
  },

  /**
   @method _calculateBottomAlignedPosition
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode.
   @param viewport {Object} Info about the page's viewport
   @private
   @return {Object} With the top and left coordinates of the contentNode and a class to apply with it.
   **/
  _calculateBottomAlignedPosition: function _calculateBottomAlignedPosition(launchNodeRegion, contentNodeRegion, viewport) {
    return {
      x: this._calculateHorizontalPos(launchNodeRegion, contentNodeRegion, viewport),
      y: launchNodeRegion.bottom + SPACING,
      placement: 'bottom'
    };
  },

  /**
   @method _calculateTopAlignedPosition
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode.
   @param viewport {Object} Info about the page's viewport
   @private
   @return {Object} With the top and left coordinates of the contentNode and a class to apply with it.
   **/
  _calculateTopAlignedPosition: function _calculateTopAlignedPosition(launchNodeRegion, contentNodeRegion, viewport) {
    return {
      x: this._calculateHorizontalPos(launchNodeRegion, contentNodeRegion, viewport),
      y: launchNodeRegion.top - SPACING - contentNodeRegion.height,
      placement: 'top'
    };
  },

  /**
   @method _calculateVerticalPos
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode container node.
   @private
   @return Number The shared y-coordinate for top and bottom aligned
   **/
  _calculateVerticalPos: function _calculateVerticalPos(launchNodeRegion, contentNodeRegion) {
    return launchNodeRegion.top + launchNodeRegion.height / 2 - contentNodeRegion.height / 2;
  },

  /**
   @method _calculateHorizontalPos
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param contentNodeRegion {Object} The region object for the contentNode.
   @param viewport {Object} Info about the page's viewport
   @private
   @return Number The shared x-coordinate for left and right aligned contentNodes
   **/
  _calculateHorizontalPos: function _calculateHorizontalPos(launchNodeRegion, contentNodeRegion, viewport) {
    return viewport.width < 481 ? 0 : Math.max(launchNodeRegion.left + launchNodeRegion.width / 2 - contentNodeRegion.width / 2, 0);
  },

  /**
   @method _repositionArrowHorizontal
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param posObj {Object} Contains the calculated coordinates of the contentNode.
   @private
   **/
  _repositionArrowHorizontal: function _repositionArrowHorizontal(launchNodeRegion, posObj) {
    var targetXVal = launchNodeRegion.width / 2 + launchNodeRegion.left,
        contentNodeXVal;
    contentNodeXVal = targetXVal - posObj.x;
    return {
      left: contentNodeXVal,
      top: null,
      transform: contentNodeXVal + (posObj.placement === 'top' ? 'px bottom' : 'px top')
    };
  },

  /**
   /**
   @method _repositionArrowVertical
   @param launchNodeRegion {Object} The region object for the launchNode.
   @param posObj {Object} Contains the calculated coordinates of the contentNode.
   @private
   **/
  _repositionArrowVertical: function _repositionArrowVertical(launchNodeRegion, posObj) {
    var targetYVal = launchNodeRegion.height / 2 + launchNodeRegion.top,
        contentNodeYVal;
    contentNodeYVal = targetYVal - posObj.y;
    return {
      top: contentNodeYVal,
      left: null,
      transform: (posObj.placement === 'left' ? ' right ' : ' left ') + contentNodeYVal + 'px'
    };
  }
};
exports["default"] = _default;


//////////////////
// WEBPACK FOOTER
// ./~/@zillow/react-zsg/Tooltip/tooltipPositioner.js
// module id = 503
// module chunks = 0